/**
 * highlight.js — Shared highlight / unhighlight utilities
 *
 * Three flavours:
 *   1. highlightSimple   – basic case-insensitive highlight (used by table search)
 *   2. highlightAccent   – accent-aware highlight with normalizeWithMap (used by page search & accordion)
 *   3. unhighlightClass  – generic unhighlight by class name
 */

import { escapeRegExp, normalizeWithMap } from "./normalize";

/* ================================================================
   1. SIMPLE highlight (table search)
      - wraps matches in <span class="highlight">
      - walks only text nodes (safe for nested HTML)
   ================================================================ */

export function highlightSimple(scopeEl, term) {
	if (!term) return;
	const regex = new RegExp(`(${escapeRegExp(term)})`, "gi");

	const walker = document.createTreeWalker(
		scopeEl,
		NodeFilter.SHOW_TEXT,
		{
			acceptNode: (node) =>
				node.nodeValue && node.nodeValue.trim()
					? NodeFilter.FILTER_ACCEPT
					: NodeFilter.FILTER_REJECT,
		},
		false
	);

	const nodes = [];
	while (walker.nextNode()) nodes.push(walker.currentNode);

	nodes.forEach((textNode) => {
		const parts = textNode.nodeValue.split(regex);
		if (parts.length === 1) return;

		const frag = document.createDocumentFragment();
		parts.forEach((part, i) => {
			if (i % 2 === 1) {
				const span = document.createElement("span");
				span.className = "highlight";
				span.textContent = part;
				frag.appendChild(span);
			} else {
				frag.appendChild(document.createTextNode(part));
			}
		});
		textNode.parentNode.replaceChild(frag, textNode);
	});
}

/** Remove <span class="highlight"> wrappers inside scopeEl */
export function unhighlightSimple(scopeEl) {
	scopeEl.querySelectorAll("span.highlight").forEach((span) => {
		span.replaceWith(document.createTextNode(span.textContent));
	});
}

/* ================================================================
   2. ACCENT-AWARE highlight (page search & accordion search)
      - uses normalizeWithMap for correct accent mapping
      - wraps matches in <mark class="{ className }">
      - accepts an array of compiled RegExp (termRegexes)
      - optional `excludeEl` to skip (e.g. the search widget itself)
   ================================================================ */

export function highlightAccent(rootEl, termRegexes, className = "search-hit", excludeEl = null) {
	if (!rootEl) return;
	termRegexes.forEach((rx) => (rx.lastIndex = 0));

	const walker = document.createTreeWalker(rootEl, NodeFilter.SHOW_TEXT, {
		acceptNode: (n) => {
			if (!n.nodeValue || !n.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
			if (excludeEl && excludeEl.contains(n.parentNode)) return NodeFilter.FILTER_REJECT;
			return NodeFilter.FILTER_ACCEPT;
		},
	});

	const nodes = [];
	while (walker.nextNode()) nodes.push(walker.currentNode);

	nodes.forEach((node) => {
		const original = node.nodeValue;
		const { norm, map } = normalizeWithMap(original);
		const ranges = [];

		termRegexes.forEach((rx) => {
			rx.lastIndex = 0;
			let m;
			while ((m = rx.exec(norm)) !== null) {
				const startN = m.index;
				const endN = startN + m[0].length;
				const startO = map[startN];
				const endO = map[endN - 1] + 1;
				if (startO != null && endO != null && endO > startO)
					ranges.push([startO, endO]);
			}
		});
		if (!ranges.length) return;

		// merge overlapping ranges
		ranges.sort((a, b) => a[0] - b[0]);
		const merged = [];
		for (const r of ranges) {
			if (!merged.length || r[0] > merged[merged.length - 1][1])
				merged.push(r.slice());
			else
				merged[merged.length - 1][1] = Math.max(merged[merged.length - 1][1], r[1]);
		}

		const frag = document.createDocumentFragment();
		let last = 0;
		for (const [s, e] of merged) {
			if (s > last) frag.appendChild(document.createTextNode(original.slice(last, s)));
			const mark = document.createElement("mark");
			mark.className = className;
			mark.textContent = original.slice(s, e);
			frag.appendChild(mark);
			last = e;
		}
		if (last < original.length) frag.appendChild(document.createTextNode(original.slice(last)));
		node.parentNode.replaceChild(frag, node);
	});
}

/** Remove <mark> wrappers by class, then normalize text nodes */
export function unhighlightAccent(root, className = "search-hit") {
	// accept both a jQuery object and a plain DOM element
	const el = root && root.jquery ? root[0] : root;
	if (!el) return;

	el.querySelectorAll(`mark.${className}`).forEach((mark) => {
		const parent = mark.parentNode;
		parent.replaceChild(document.createTextNode(mark.textContent), mark);
		parent.normalize();
	});
}

/**
 * Convenience wrapper used by accordion search:
 * highlight inside a jQuery-wrapped element using termRegexes.
 */
export function highlightJQ($el, termRegexes, className = "search-hit") {
	const el = $el && $el.jquery ? $el[0] : $el;
	highlightAccent(el, termRegexes, className);
}

export function unhighlightJQ($el, className = "search-hit") {
	unhighlightAccent($el, className);
}
